import { useEffect, useRef, useState } from "react";
import { CaretLeft, CheckCircle, CircleNotch, Envelope, Key, UserPlus } from "@phosphor-icons/react";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useGuestOtpAuth } from "@/hooks/useGuestOtpAuth";
import type { OtpPhase } from "@/lib/otp-auth-machine";

const OTP_LENGTH = 6;

export interface TherapistEnrollOtpGateOtp {
  phase: OtpPhase;
  email: string;
  errorKey: string | null;
  cooldown: number;
  setEmail: (email: string) => void;
  sendCode: () => Promise<void>;
  resend: () => Promise<void>;
  verifyCode: (otp: string) => Promise<void>;
  editEmail: () => void;
}

interface TherapistEnrollOtpGateProps {
  onAuthenticated: () => void;
}

interface TherapistEnrollOtpGateViewProps {
  otp: TherapistEnrollOtpGateOtp;
  code: string;
  setCode: (code: string) => void;
}

export function TherapistEnrollOtpGate({ onAuthenticated }: TherapistEnrollOtpGateProps) {
  const otp = useGuestOtpAuth(onAuthenticated, { completeClientProfile: false });
  const [code, setCode] = useState("");
  const autoSubmittedCodeRef = useRef("");

  const verifying = otp.phase === "verifying";

  useEffect(() => {
    if (otp.phase === "code" && otp.errorKey) {
      setCode("");
    }
  }, [otp.phase, otp.errorKey]);

  useEffect(() => {
    if (code.length < OTP_LENGTH) {
      autoSubmittedCodeRef.current = "";
      return;
    }
    if (
      otp.phase !== "code" ||
      verifying ||
      autoSubmittedCodeRef.current === code
    ) {
      return;
    }
    autoSubmittedCodeRef.current = code;
    void otp.verifyCode(code);
  }, [otp, code, verifying]);

  return (
    <TherapistEnrollOtpGateView
      otp={otp}
      code={code}
      setCode={setCode}
    />
  );
}

export function TherapistEnrollOtpGateView({
  otp,
  code,
  setCode,
}: TherapistEnrollOtpGateViewProps) {
  const collecting = otp.phase === "collect" || otp.phase === "sending";
  const sending = otp.phase === "sending";
  const verifying = otp.phase === "verifying";
  const canSend = /\S+@\S+\.\S+/.test(otp.email.trim());

  if (collecting) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-muted/40 via-background to-primary/3 px-4 py-10 sm:py-16">
        <section className="mx-auto flex min-h-[72vh] max-w-lg flex-col justify-center">
          <div className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
            <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <UserPlus className="size-6" />
            </div>

            <div className="mt-6 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-primary">
                Prijava za terapeuta
              </p>
              <h1
                className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Hajde prvo da vas registrujemo
              </h1>
              <p className="text-sm leading-6 text-muted-foreground">
                Upišite email adresu. Poslat ćemo vam 6-cifreni kod za potvrdu,
                pa odmah nastavljate na formular za prijavu terapeuta.
              </p>
            </div>

            <div className="mt-6 space-y-4">
              <label htmlFor="therapist-enroll-email" className="block text-sm font-medium text-foreground">
                Email adresa
              </label>
              <input
                id="therapist-enroll-email"
                type="email"
                value={otp.email}
                onChange={(event) => otp.setEmail(event.target.value)}
                placeholder="vas@email.com"
                autoComplete="email"
                className="h-12 w-full rounded-xl border border-input bg-background px-4 text-sm outline-none transition focus:border-primary focus:ring-[3px] focus:ring-primary/15"
              />

              <div className="flex items-start gap-2 rounded-xl bg-primary/8 px-3 py-2.5 text-xs leading-5 text-muted-foreground">
                <Key className="mt-0.5 size-4 shrink-0 text-primary" />
                <span>
                  Ako već imate nalog, isti kod će vas samo prijaviti. Ako nemate,
                  kreirat ćemo nalog bez lozinke.
                </span>
              </div>

              {otp.errorKey ? (
                <p className="text-sm text-destructive">{otpErrorText(otp.errorKey)}</p>
              ) : null}

              <Button
                type="button"
                size="lg"
                className="w-full"
                disabled={!canSend || sending}
                onClick={otp.sendCode}
              >
                {sending ? (
                  <CircleNotch className="size-4 animate-spin" />
                ) : (
                  <Envelope className="size-4" />
                )}
                {sending ? "Šaljem kod..." : "Pošalji kod"}
              </Button>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const resendText =
    otp.cooldown > 0
      ? `Pošalji ponovo · 0:${String(otp.cooldown).padStart(2, "0")}`
      : "Pošalji ponovo";

  return (
    <main className="min-h-screen bg-gradient-to-br from-muted/40 via-background to-primary/3 px-4 py-10 sm:py-16">
      <section className="mx-auto flex min-h-[72vh] max-w-lg flex-col justify-center">
        <div className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
          <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Envelope className="size-6" />
          </div>

          <div className="mt-6 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-primary">
              Provjera emaila
            </p>
            <h1
              className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Upišite kod iz emaila
            </h1>
            <p className="text-sm leading-6 text-muted-foreground">
              Poslali smo 6-cifreni kod na <strong className="text-foreground">{otp.email}</strong>.
              Nakon potvrde otvaramo formular za prijavu.
            </p>
          </div>

          <div className="mt-6 space-y-4">
            <OtpCodeInput
              id="therapist-enroll-otp"
              value={code}
              onChange={setCode}
              invalid={!!otp.errorKey}
              autoFocus
              className="mx-auto max-w-[22rem]"
            />

            {otp.errorKey ? (
              <p className="text-center text-sm text-destructive">{otpErrorText(otp.errorKey)}</p>
            ) : null}

            <p className="text-center text-sm text-muted-foreground">
              Niste dobili kod?{" "}
              <button
                type="button"
                onClick={otp.resend}
                disabled={otp.cooldown > 0}
                className={cn(
                  "font-medium",
                  otp.cooldown > 0 ? "text-muted-foreground" : "text-primary underline",
                )}
              >
                {resendText}
              </button>
            </p>

            <Button
              type="button"
              size="lg"
              className="w-full"
              disabled={code.length < OTP_LENGTH || verifying}
              onClick={() => otp.verifyCode(code)}
            >
              {verifying ? (
                <CircleNotch className="size-4 animate-spin" />
              ) : (
                <CheckCircle className="size-4" />
              )}
              {verifying ? "Provjeravam..." : "Nastavi na prijavu"}
            </Button>

            <button
              type="button"
              onClick={() => {
                setCode("");
                otp.editEmail();
              }}
              className="mx-auto flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <CaretLeft className="size-4" />
              Promijeni email
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

function otpErrorText(key: string): string {
  if (key.includes("Expired")) return "Kod je istekao. Zatražite novi.";
  if (key.includes("Attempts")) return "Previše pokušaja. Zatražite novi kod.";
  if (key.includes("Wrong")) return "Netačan kod. Pokušajte ponovo.";
  if (key.includes("Send")) return "Slanje koda nije uspjelo. Pokušajte ponovo.";
  return "Nešto je pošlo po zlu. Pokušajte ponovo.";
}
